// Generated by tutorials/record.cjs from tutorials/articles/h48-importing-a-menu.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h48 --text-only` (wording) or
// `node tutorials/record.cjs h48` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'importing-a-menu',
  catalogId: 'h48',
  title: 'Importing a menu',
  summary: 'Turn a menu package from your consultant dietitian or menu service into a new menu cycle: add its two spreadsheets, check what they say, match each dish to one of your items, and build. The new menu stays inactive until you have reviewed it.',
  audience: 'Menu planners and dietary managers who receive a written menu each season',
  area: 'menus',
  learn: [
    'What a menu package is and which two files to add',
    'Check the days, meals and diet guidance before matching',
    'Match each dish on the menu to one of your items',
    'Choose the closest of your menus as the template',
    'Build the new menu cycle and open it to review',
  ],
  keywords: [
    'import',
    'menu import',
    'vendor menu',
    'menu package',
    'week at a glance',
    'diet guide',
    'diet guides',
    'extension',
    'spreadsheet',
    'excel',
    'pdf',
    'consultant',
    'dietitian',
    'match',
    'matching',
    'new menu cycle',
    'seasonal menu',
    'template',
  ],
  steps: [
    {
      id: 'open',
      part: 'Add the menu package',
      heading: 'Open Menu Import',
      text: 'On the Menu Cycles page, select Import Menu. You can also find Menu Import under Menus in the menu.\n\nA menu package has two files: the Week At A Glance (what is served each day) and the Diet Guides (what each diet and texture gets instead). The bar at the top shows the five stages of an import. Imports in progress lists the imports you or a colleague started here; select Resume to carry on with one.',
      media: {
        image: {
          src: '/help/importing-a-menu/open.webp',
          width: 1280,
          height: 842,
        },
        alt: 'Menu Import on its Files stage, with the five stages across the top, an earlier import listed under Imports in progress, and the box for the menu files.',
      },
    },
    {
      id: 'source',
      part: 'Add the menu package',
      heading: 'Pick the menu source',
      text: 'Under Menu source, pick who wrote the menu: your consultant dietitian, a menu service, or your own team. Choose + New menu source to type a new one.\n\nThe matches you approve are remembered under this name, so keep it the same every season.',
      media: {
        image: {
          src: '/help/importing-a-menu/source.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/importing-a-menu/source.mp4',
          width: 1280,
          height: 842,
          durationSec: 3.9,
        },
        alt: 'Lakeside Dietitian Services picked as the menu source, above the box for the menu files.',
      },
    },
    {
      id: 'files',
      part: 'Add the menu package',
      heading: 'Add the two files',
      text: 'Drop both files on the box, or select Choose files and pick them. The page reads them on your computer; it takes a few seconds.\n\nEach file shows a check mark once it is read, with how many meals, weeks, rows and diet columns it found.',
      media: {
        image: {
          src: '/help/importing-a-menu/files.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/importing-a-menu/files.mp4',
          width: 1280,
          height: 842,
          durationSec: 2.4,
        },
        alt: 'Both files read: the Week At A Glance with 84 meals over 4 weeks and the Diet Guides with its rows and 6 columns.',
      },
    },
    {
      id: 'review',
      part: 'Check what the package says',
      heading: 'Review the package',
      text: 'Select Next: Review. The top line counts the days, main-plate dishes, alternates and names to match. The table shows what each diet column does with the dishes: serve them as is, change the texture, serve another item, or not serve them.\n\nWhat to check in the package lists anything the files did not say clearly, such as a dish with no diet guidance.',
      media: {
        image: {
          src: '/help/importing-a-menu/review.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/importing-a-menu/review.mp4',
          width: 1280,
          height: 842,
          durationSec: 2.6,
        },
        alt: 'The Review stage: 28 days, the counts of dishes and names to match, and a table of what the Cardiac, Consistent Carb, Renal, Mech Soft and Pureed columns do.',
      },
    },
    {
      id: 'week',
      part: 'Check what the package says',
      heading: 'See the menu as a diet gets it',
      text: 'Further down, each week shows as a grid of meals. Pick a diet or texture in the list above it to see what that diet is served. An arrow marks a different dish, and a struck-through dish is not served on that diet.',
      media: {
        image: {
          src: '/help/importing-a-menu/week.webp',
          width: 1280,
          height: 670,
        },
        clip: {
          src: '/help/importing-a-menu/week.mp4',
          width: 1280,
          height: 670,
          durationSec: 4.8,
        },
        alt: 'Week 1 of the package shown for the Renal diet: Rice in place of Mashed Potatoes and Sweet Potatoes, and the soups struck through.',
      },
    },
    {
      id: 'match',
      part: 'Match the dishes to your items',
      heading: 'Match the names to your items',
      text: 'Select Next: Match items, then Match items. Each name on the menu is compared with your items. Names approved on an earlier import from this source, and names exactly the same as one of your items, are approved for you.\n\nNeeds attention lists the rest. Select a name to see the item chosen for it on the right.',
      media: {
        image: {
          src: '/help/importing-a-menu/match.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/importing-a-menu/match.mp4',
          width: 1280,
          height: 842,
          durationSec: 5.3,
        },
        alt: 'The Match items stage after matching: Needs attention lists Mashed Potatoes, Glazed Carrots, Egg Substitute and Cranberry Relish, with the first one open on the right.',
      },
    },
    {
      id: 'approve',
      part: 'Match the dishes to your items',
      heading: 'Approve a good match',
      text: 'Mashed Potatoes was matched to POT MASHED, the closest name, but nobody has checked it yet. When the Dietech item is right, select Approve and next. The name leaves the list and the next one opens.\n\nWhere it is on the menu lists every day and meal that serves the dish, to help you decide.',
      media: {
        image: {
          src: '/help/importing-a-menu/approve.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/importing-a-menu/approve.mp4',
          width: 1280,
          height: 842,
          durationSec: 4.7,
        },
        alt: 'Mashed Potatoes approved: it has left Needs attention, Undo: Approved Mashed Potatoes is offered, and the next name is open on the right.',
      },
    },
    {
      id: 'pick',
      part: 'Match the dishes to your items',
      heading: 'Choose a better item',
      text: 'Glazed Carrots was matched to CARROTS SLICED, which is not the same dish. Open the Dietech item list and pick the right one from the closest names. To look through all your items, type in Search all items.\n\nThen select Approve and next.',
      media: {
        image: {
          src: '/help/importing-a-menu/pick.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/importing-a-menu/pick.mp4',
          width: 1280,
          height: 842,
          durationSec: 6,
        },
        alt: 'Glazed Carrots with CARROTS GLAZED now chosen as the Dietech item, ready to approve.',
      },
    },
    {
      id: 'leave-out',
      part: 'Match the dishes to your items',
      heading: 'Leave out a dish you do not serve',
      text: 'Cranberry Relish matched none of your items. Choose an item, select New item to make one, or leave it out of the menu.\n\nTo leave it out, select Leave out, give a reason so the next person knows it was on purpose, and select Leave out again.',
      media: {
        image: {
          src: '/help/importing-a-menu/leave-out.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/importing-a-menu/leave-out.mp4',
          width: 1280,
          height: 842,
          durationSec: 6,
        },
        alt: 'Cranberry Relish with the reason Served from stock, not a menu item typed, and the Leave out button.',
      },
    },
    {
      id: 'replacement',
      part: 'Match the dishes to your items',
      heading: 'Approve a diet replacement',
      text: 'Some names are not on the menu itself but in a diet column: Egg Substitute is what the Cardiac diet gets in place of eggs. Match them the same way.\n\nWhen the item is right, you can also check the box at the end of its row to approve it. Once Needs attention is empty, every name has a decision.',
      media: {
        image: {
          src: '/help/importing-a-menu/replacement.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/importing-a-menu/replacement.mp4',
          width: 1280,
          height: 842,
          durationSec: 5.9,
        },
        alt: 'Egg Substitute approved with the check mark at the end of its row, and Needs attention down to none.',
      },
    },
    {
      id: 'compare',
      part: 'Set up the new menu',
      heading: 'Choose the closest menu as the template',
      text: 'Select Next: Configure. The new menu takes its settings, diet and texture columns and standard rows (beverages, milk, condiments) from one of your menu cycles, the template.\n\nWhich of your menus is closest compares the package with each of your menus. The closest is usually the best template. Select Use as template on it.',
      media: {
        image: {
          src: '/help/importing-a-menu/compare.webp',
          width: 1280,
          height: 450,
        },
        clip: {
          src: '/help/importing-a-menu/compare.mp4',
          width: 1280,
          height: 450,
          durationSec: 3.2,
        },
        alt: 'Which of your menus is closest: Fall/Winter Cycle 2026 marked Closest, with 78% of the same meals, and now marked Template.',
      },
    },
    {
      id: 'name',
      part: 'Set up the new menu',
      heading: 'Name the new menu',
      text: 'Under The new menu, check the template, type the new menu\'s name, and pick its start date.\n\nRun AIM fills the diet columns of items you have never served before. Leave it off when most of the dishes are already on your menus.',
      media: {
        image: {
          src: '/help/importing-a-menu/name.webp',
          width: 1280,
          height: 152,
        },
        clip: {
          src: '/help/importing-a-menu/name.mp4',
          width: 1280,
          height: 152,
          durationSec: 4.9,
        },
        alt: 'The new menu: Fall/Winter Cycle 2026 as the template, the name Fall/Winter Cycle 2027 and a start date.',
      },
    },
    {
      id: 'columns',
      part: 'Set up the new menu',
      heading: 'Check where each column gets its values',
      text: 'Each of your diet and texture columns is matched to the column of the package with the same diet. A column with no match keeps how you already serve each item.\n\nChange a match in the list beside it if the package calls a diet something else.',
      media: {
        image: {
          src: '/help/importing-a-menu/columns.webp',
          width: 1280,
          height: 414,
        },
        alt: 'Your columns Regular, Cardiac, Consistent Carbohydrate, Renal, Mechanical Soft and Pureed each taking the package column of the same diet; Low Sodium 2g and Liberal Geriatric not in the package.',
      },
    },
    {
      id: 'ready',
      part: 'Build the menu cycle',
      heading: 'Check it is ready to build',
      text: 'Select Next: Build. The top line counts the meals, item rows and diet cells the new menu will have, and the preview shows each day as it will be built.\n\nReady to build means nothing is in the way. If something is, it is listed with a button that takes you to fix it.',
      media: {
        image: {
          src: '/help/importing-a-menu/ready.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/importing-a-menu/ready.mp4',
          width: 1280,
          height: 842,
          durationSec: 5.6,
        },
        alt: 'The foot of the Build stage: Ready to build, noting the one name left out on purpose, and the Build button.',
      },
    },
    {
      id: 'build',
      part: 'Build the menu cycle',
      heading: 'Build it',
      text: 'Select Build. It takes up to a minute; the bar shows how far it has got.\n\nThe new menu cycle is not active for anyone yet. Select Open in Modify to look through it, then make it active on the Menu Cycles page when it is ready.',
      media: {
        image: {
          src: '/help/importing-a-menu/build.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/importing-a-menu/build.mp4',
          width: 1280,
          height: 842,
          durationSec: 5,
        },
        alt: 'Fall/Winter Cycle 2027 built as a new menu, every imported row checked in the saved menu, with Open in Modify.',
      },
    },
  ],
  tips: [
    'Pick the menu source from the list when it is one you have used before. The matches you approve are remembered under it, so next season most of the names match themselves.',
    'In the match list, Enter approves the selected name and moves to the next one, L leaves it out, and U undoes. You can work through a long menu without the mouse.',
    'Approve sure matches approves, in one go, every name matched exactly or remembered from an earlier import. Guesses are always approved one at a time.',
    'The import saves as you go. Close the page and come back later, or let a colleague carry on: it is listed under Imports in progress.',
  ],
  notes: [
    'The files can be Excel spreadsheets (.xlsx) or PDFs. Add both at once, or one after the other.',
    'Bold dishes on the Week At A Glance are the main plate and plain ones the alternate. Beverages and condiments come from the template\'s standard rows, not from the package.',
    'Building creates a new menu cycle. It does not change any menu you already have, and it is not active until you make it active on the Menu Cycles page.',
    'Building needs menu cycle edit rights. Anyone who can open Menu Cycles can read a package and match its names.',
  ],
  related: [
    'menu-cycles',
    'reading-a-menu-cycle',
    'changing-menu-items',
    'diet-and-consistency-rules',
    'menu-analysis',
  ],
  recorded: '2026-10-26',
};

export default article;
